'use client';

import {useTranslations} from 'next-intl';
import {type FC, memo} from 'react';
import {StorageImage} from '@/components/atoms/StorageImage';
import {ReferralLinkInput} from '@/entities/User/ui/UserInfo/ReferralLinkInput';
import {Modal} from '@/utils/antd-imports';
import type {ActionCardProps} from './ActionCard.types';

interface CelebrityShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  celebrity?: ActionCardProps['celebrity'];
  /** Share URL from API (POST celebrity-share). */
  shareUrl: string;
}

/**
 * @description Share modal for platform_share_celebrity. Figma 2825-262243.
 * Avatar, "Look who...", description, link field + social buttons.
 */
export const CelebrityShareModal: FC<CelebrityShareModalProps> = memo(({isOpen, onClose, celebrity, shareUrl}) => {
  const t = useTranslations('experiences.card');

  const celebrityName = celebrity ? `${celebrity.firstName} ${celebrity.lastName}` : '';

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closeIcon={false}
      centered
      className="[&_.ant-modal-content]:!bg-transparent [&_.ant-modal-content]:!p-0 [&_.ant-modal-wrap]:!bg-black/60 [&_.ant-modal-content]:max-w-[288px] md:[&_.ant-modal-content]:max-w-[560px] [&_.ant-modal-content]:mx-auto [&_.ant-modal-content]:w-full"
      styles={{
        body: {padding: 0},
      }}
      width={'100%'}
    >
      <div className="relative flex flex-col items-center gap-4 rounded-[24px] bg-[#1E1E2A] px-6 py-8 md:gap-6 md:px-8 md:py-12 font-['Inter']">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 flex size-10 items-center justify-center rounded-full border border-[rgba(233,236,240,0.2)] p-2 transition-opacity hover:opacity-80 md:right-4 md:top-4"
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <title>Close</title>
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="#E9ECF0"
              strokeWidth="1.35"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className="flex flex-col items-center gap-3 px-9 md:gap-4 md:px-12">
          <div className="relative flex items-end justify-end">
            {celebrity?.headerAvatarImg && (
              <div className="relative size-14 shrink-0 overflow-hidden rounded-full border border-black/10 md:size-[72px] md:-mr-4">
                <StorageImage
                  src={celebrity.headerAvatarImg}
                  alt={celebrityName}
                  className="h-full w-full object-cover"
                  withBlur
                  sources={[
                    {media: '(min-width: 1280px)', width: 56, height: 56, resize: 'cover'},
                    {media: '(min-width: 768px)', width: 56, height: 56, resize: 'cover'},
                    {media: '(min-width: 0px)', width: 56, height: 56, resize: 'cover'},
                  ]}
                />
              </div>
            )}
            <div className="-ml-3 flex size-11 items-center justify-center rounded-full border border-[rgba(61,99,232,0.1)] bg-[rgba(53,46,115,0.35)] backdrop-blur-sm md:-mr-4 md:size-[60px] md:rounded-[60px]">
              <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <title>Celebrity Share</title>
                <path
                  d="M22.1502 3.53125H2.28906L5.3756 8.96622H12.2991L5.4427 20.9097H12.1526L22.1502 3.53125Z"
                  fill="#0091FF"
                />
                <path
                  d="M22.1449 3.53125H15.435L12.3485 8.96622L8.85938 14.938L12.2143 20.9097L22.1449 3.53125Z"
                  fill="#00B9FF"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-center font-['inter'] text-[20px] font-bold leading-7 text-[#E9ECF0] md:text-[36px] md:leading-normal">
            {t('look_who')}
          </h2>
        </div>

        <div className="flex w-full flex-col gap-6 md:max-w-[406px] md:gap-8">
          <p className="text-center font-['inter'] text-[16px] font-medium leading-5 text-[rgba(233,236,240,0.8)] md:text-[20px] md:leading-[1.2]">
            {t('is_selling_on_myseven_now', {name: celebrityName})}
          </p>
          <ReferralLinkInput className="w-full md:max-w-[336px] mx-auto" initialLink={shareUrl} variant="modal" />
        </div>
      </div>
    </Modal>
  );
});

CelebrityShareModal.displayName = 'CelebrityShareModal';
